import { apiAgent } from "../../config/axiosConfig";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  updateLoadError,
  setLoadingState,
  setSavingState,
} from "./testRulesSlice";

const createTestRule = createAsyncThunk(
  "createTestRule",
  async (
    { testCaseSetId, expression, title, ruleType  },
    { dispatch, rejectWithValue }
  ) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_rules/",
        data: {
          test_case_set_id: testCaseSetId,
          expression: expression,
          title: title,
          rule_type: ruleType
        },
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      } else {
        await dispatch(setLoadingState(false));
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const getTestRule = createAsyncThunk(
  "getTestRule",
  async ({ testRuleId }, { dispatch, rejectWithValue }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/test_rules/?test_rule_id=${testRuleId}`,
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      await dispatch(setLoadingState(false));
    }
  }
);

const getAllTestRules = createAsyncThunk(
  "getAllTestRules",
  async ({ testCaseSetId }, { dispatch, rejectWithValue }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/test_rules/?test_case_set_id=${testCaseSetId}`,
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data?.test_rules;
      }
    } catch (err) {
      await dispatch(setLoadingState(false));
      dispatch(
        updateLoadError({
          error_message:
            err.response?.data?.message || "Failed to fetch test rules",
        })
      );
      return rejectWithValue(err.response?.data);
    }
  }
);

const updateTestRule = createAsyncThunk(
  "updateTestRule",
  async ({ testRuleId, data }, { dispatch, rejectWithValue }) => {
    await dispatch(setSavingState(true));
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/test_rules/${testRuleId}`,
        data: data,
      });
      if (response.status === 200) {
        await dispatch(setSavingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const deleteTestRule = createAsyncThunk(
  "deleteTestRule",
  async ({ testRuleId }, { dispatch, rejectWithValue }) => {
    await dispatch(setSavingState(true));
    try {
      const response = await apiAgent({
        method: "DELETE",
        url: `/api/v1/test_rules/${testRuleId}`,
      });
      if (response.status === 200) {
        await dispatch(setSavingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const generateRuleWithAI = createAsyncThunk(
  "generateRuleWithAI",
  async (
    { ruleText, testCaseSetId, ruleType },
    { dispatch, rejectWithValue }
  ) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_rules/generate_by_ai",
        data: {
          test_case_set_id: testCaseSetId,
          rule_text: ruleText,
          rule_type: ruleType,
        },
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      }
    } catch (err) {
      await dispatch(setLoadingState(false));
      return rejectWithValue(err?.payload?.response);
    }
  }
);

const getAllSuggestedRules = createAsyncThunk(
  "getAllSuggestedRules",
  async ({ testCaseSetId }, { dispatch, rejectWithValue }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/suggested_test_rules/?test_case_set_id=${testCaseSetId}`,
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data?.test_rules;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      await dispatch(setLoadingState(false));
    }
  }
);

export {
  createTestRule,
  getTestRule,
  getAllTestRules,
  updateTestRule,
  deleteTestRule,
  generateRuleWithAI,
  getAllSuggestedRules,
};
